/**
 * mcp-application.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component, ElementRef, HostListener, Inject, OnInit }
  from "@angular/core";
import { Router } from "@angular/router";
import { Modal } from "angular2-modal/plugins/bootstrap";

import { Observable } from "rxjs/Rx";

import { UserService } from "./auth/identity/user.service";
import { BrandingService } from "./common/branding.service";
import { ShortcutHelpModalComponent } from "./common/shortcut-help-modal.component";
import { ModalService } from "./common/modal.service";

import { ConfigurationService } from "./configuration/configuration.service";
import { NavigationHistoryService } from "./debug/navigation-history.service";
import { SupportService } from "./debug/support.service";
import { LAUNCH_DEPENDENCIES } from "./launch-dependencies";
import { AddReminderModalComponent, ReminderModalResult }
  from "./watchlist/add-reminder-modal.component";
import { Reminder } from "./watchlist/reminder";
import { WatchlistService } from "./watchlist/watchlist.service";
import { SoundsService, SoundEffect } from "./support/APIs/sounds.service";

// TODO: Branch styles into its own module?
import "./styles.sass";
import { oneline, jsonify } from "./support/utilities";
import { MessagingService } from "./server/messaging.service";
import { DebugLogUpstreamMessage } from "./server/messages/debug";
import { CONSOLE_HISTORY } from "./debug/console-interceptor";
import { IframeModalComponent } from "./common/iframe-modal-dialog.component";
import { AlertService } from "./common/alert.service";
import { AlgorithmService } from "./algorithms/management/algorithm.service";
import { SettingService } from "./configuration/setting.service";

@Component({
  selector: "mcp-application",
  template: `
  <!-- at present our application just contains the "outlet" of the router
  but eventually there will probably be styling at this level -->
  <div class="alert-container" [class.bottom-alerts]="settings.getOr('alert position', 'top') === 'bottom'"><alert-list></alert-list></div>
  <article defaultOverlayTarget [class.developer]="users.self.dev">
    <router-outlet></router-outlet>
  </article>
  <navbar></navbar>
  <footer>
    <template [ngIf]="branding.strings.footer">
      <p>{{branding.strings.footer}}</p>
      <p> ❧ </p>
    </template>
    <p>
      ©2016-2024 My Coverage Plan, Inc. | Patent #8126727
    </p>
  </footer>
  <div id="media-preload">
    <audio *ngFor="let audio of audioPreloads" [id]="audio[0]" [src]="audio[1]">
    </audio>
  </div>
  `,
  styles: [
    `article {position: relative; margin-top: 71px;}
    @media print {
      article {margin-top: 0px;}
    }
    footer {
      margin: 1.5em auto;
      position: relative;
      z-index: -1000;
    }
    .alert-container {
      position: fixed;
      top: 72px;
      z-index: 20;
      width: 100%;
      text-align: center;
      pointer-events: none;
    }
    .alert-container.bottom-alerts {
      top: unset;
      bottom: 20px;
    }
    #media-preload {
      display: none;
    }`
  ]
})
export class MCPApplicationComponent
{
  /**
   * Construct a new {@link MCPApplicationComponent}.
   */
  public constructor (
    @Inject(LAUNCH_DEPENDENCIES) ld : any,
    public users : UserService,
    public modal : ModalService,
    public el : ElementRef,
    public config : ConfigurationService,
    public branding : BrandingService,
    support : SupportService,
    navHistory : NavigationHistoryService,
    public watchlist : WatchlistService,
    public messaging : MessagingService,
    public algorithms : AlgorithmService,
    public settings : SettingService
  ) {
    navHistory.logAll();
    support.checkBrowser();
    this.users.ready.subscribe(
      (loggedIn : boolean) => this.shortcutsEnabled = loggedIn);
    // TODO: Investigate moving such startup operations to module level?
    // NOTE: Since the services live in multiple modules, it might be better
    // to use something like APP_INITIALIZER?
  }

  public audioPreloads : Array<[SoundEffect, string]> =
    Array.from(SoundsService.PreloadMedia.entries());

  private shortcutsEnabled : boolean = false;

  /**
   * Create a Reminder using the global keyboard shortcut.
   *
   * @param event Key event triggering the shortcut
   */
  @HostListener("document:keydown.control.m", ["$event"])
  @HostListener("document:keydown.meta.m", ["$event"])
  public addReminder (event : KeyboardEvent & {targetDate? : Date}) : void
  {
    if (!this.shortcutsEnabled)
    {
      return;
    }
    event.preventDefault();
    this.modal.observe<ReminderModalResult>(
      AddReminderModalComponent,
      {
        size: "md",
        initialDate: event.targetDate,
        hasImpliedLink: this.algorithms.isRunning
      }
    ).subscribe((r : ReminderModalResult | null) => {
      if (r)
      {
        this.watchlist.addReminder(
          r.summary,
          r.triggerTime,
          r.linkedRecord ? this.algorithms.running.subject.id : undefined
        ).subscribe();
      }
    });
  }

  /**
   * Display the shortcut help modal.
   *
   * @param event Key event triggering the shortcut
   */
  // @HostListener("document:keydown.f1", ["$event"])
  // public displayShortcutHelp (event : KeyboardEvent) : void
  // {
  //   if (!this.shortcutsEnabled)
  //   {
  //     return;
  //   }
  //   event.preventDefault();
  //   this.modal.display(
  //     ShortcutHelpModalComponent);
  // }

  public contactPageCloser : (() => void) = null;
  @HostListener("document:keydown.f5", ["$event"])
  public showContactPage (event : KeyboardEvent) : void
  {
    if (typeof this.contactPageCloser === "function") {
      this.contactPageCloser();
      this.contactPageCloser = null;
    } else {
      this.modal.display(
        IframeModalComponent,
        {url: "/zone/documents/faq.html", title: "Frequently Asked Questions"}
      ).subscribe((closeFn) => {
        this.contactPageCloser = closeFn;
      });
    }
  }

  /**
   * Attempt to file an out-of-band issue report.
   */
  public reportIssueOOB () : void
  {
    this.modal.stringPrompt({
      title: "Submit Performance / Crash Report",
      body: oneline`
        Enter a note to submit with this report, ideally describing why you are
        submitting it.`,
      placeholder: "Note (required)"
    }).subscribe(
      (note : string) => {
        if (!note) {
          return;
        }
        const recentErrors : string =
          CONSOLE_HISTORY
          .filter(
            ([e, t, a] : [string, any, any]) => (e === "warn" || e === "error"))
          .map((c : any) => jsonify(c, 200, true))
          .join("\n");
        const content : string = `
sent: ${(new Date()).toLocaleDateString}
sent by: ${this.users.self.name}
note: ${note}
recent pings: ${this.messaging.recentPingsRTT.list()}
console history: ${recentErrors}`;
        this.messaging.sendSideChannelOneOff(new DebugLogUpstreamMessage(
          "WARNING",
          content,
          true)).subscribe();
      }
    );
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/mcp-application.component.ts